import { CommonEventInit, CommonListenerOrEventListenerObject, CommonListEventHandler, eventFactory } from "@cervval.js/common/src/tools/event.tools";
import { anyReallyAny } from "@cervval.js/common/src/tools/object.tools";
import { DomTools } from "../tools";
import { WebComponent } from "./webComponent";

export { FullEventInit, NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";

export enum EventHandlerType {
    AUTO = 1,// default value : instance of HtmlElement => DOM, LIST otherwise
    DOM,
    LIST
}

/** 
 * Add component linked behavior to an event handler "on" function 
 */
export interface WebComponentEventOptions {
    /** component to "observe" (attachment to DOM or visibility) */
    component: WebComponent;

    /** 
     * linkToDom = true => the listener is removed when component is detached from DOM and added when attached to DOM 
     * if linkToDOM is used in combination with once, the event will be listened once after each DOM attachment
     */
    linkToDom: boolean;

    /** onlyIfVisible = true => the event is not triggered if component is not visible (@see DomTools.isVisible) */
    onlyIfVisible: boolean;
}

export type AddDomEventListenerOptions = boolean | AddEventListenerOptions | AddEventListenerOptions & WebComponentEventOptions;

/**
 * Property decorator that installs a EventHandler on the event property
 */
export function Event<EventInitOptions extends CommonEventInit = CommonEventInit>(defaultOptions?: EventInitOptions, type?: EventHandlerType): (classPrototype: anyReallyAny, eventKey: string) => void {
    return eventFactory((ek, el) => {
        let handlerType = type ?? EventHandlerType.AUTO;
        if (handlerType === EventHandlerType.AUTO) {
            handlerType = (el as HTMLElement)["dispatchEvent"] != null ? EventHandlerType.DOM : EventHandlerType.LIST;
        }

        if (handlerType === EventHandlerType.LIST) {
            return new ClientListEventHandler(ek, defaultOptions);
        } else {
            return new DomEventHandler(el as HTMLElement, ek, defaultOptions);
        }
    });
}

/**
 * A function that is called whenever an event of the specified type occurs.
 */
export type CustomEventListener<EventData> = (event: CustomEvent<EventData>) => void;

export interface CustomEventListenerObject<EventData> {
    /**
     * A function that is called whenever an event of the specified type occurs.
     */
    handleEvent(event: CustomEvent<EventData>): void;
}

export type CustomEventListenerOrEventListenerObject<EventData> = CustomEventListener<EventData> | CustomEventListenerObject<EventData>;

/**
 * The event handler handles add/remove listeners and emit event
 */
export interface EventHandler<EventData = void, EventInitOptions extends EventInit = EventInit> {

    /**
     * event name
     */
    event: string;

    /**
     * This can help determine if there is a listener attached to this event
     * onWasCalled is set to true if "on" function was called
     * * this does not detect calls to addEventListeners for this event
     */
    onWasCalled: boolean;

    /**
     * register listener on the event
     * @returns this event handler
     */
    on(listener: CustomEventListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): EventHandler<EventData>;

    /**
     * register listener on the event
     * @returns this event handler
     */
    off(listener: CustomEventListenerOrEventListenerObject<EventData>): EventHandler<EventData>;

    /**
     * emit CustomEvent with eventData
     */
    emit(eventData: EventData, options?: EventInitOptions): void;
}

/** used by WebComponentEventHandlerHelper  */
interface HelpedEventHandler<EventData> {

    /**
     * register listener on the event
     * @returns this event handler
     */
    addListener(listener: CustomEventListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): void;

    /**
     * register listener on the event
     * @returns this event handler
     */
    removeListener(listener: CustomEventListenerOrEventListenerObject<EventData>): void;

}

/** handle WebComponentEventOptions options */
class WebComponentEventHandlerHelper<EventData> {

    constructor(public eventHandler: HelpedEventHandler<EventData>) {

    }

    /** contains the overridden listeners (in order to "off" the listener that was "on"). Listeners are overridden to manage onlyIfVisible option. */
    protected _overriddenListenersMap: Map<CustomEventListenerOrEventListenerObject<EventData>, CustomEventListenerOrEventListenerObject<EventData>> | undefined;

    /**
     * register listener on the event
     * @returns this event handler
     */
    public on(listener: CustomEventListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): void {
        const wcOpts = options as WebComponentEventOptions | undefined;
        if (wcOpts?.component) {
            if (wcOpts.onlyIfVisible) {
                // emit event only if component is visible
                const originalListener = listener;
                if (this._overriddenListenersMap?.has(originalListener)) {
                    // already overridden
                    listener = this._overriddenListenersMap.get(originalListener)!;
                } else {
                    if ((originalListener as CustomEventListenerObject<EventData>).handleEvent) {
                        listener = (event: CustomEvent<EventData>): void => {
                            if (DomTools.isVisible(wcOpts.component)) {
                                (originalListener as CustomEventListenerObject<EventData>).handleEvent(event);
                            }
                        };
                    } else if (typeof originalListener === "function") {
                        listener = (event: CustomEvent<EventData>): void => {
                            if (DomTools.isVisible(wcOpts.component)) {
                                originalListener(event);
                            }
                        };
                    }
                    // store the overridden listeners (in order to "off" the listener that was "on")
                    this._overriddenListenersMap ??= new Map();
                    this._overriddenListenersMap.set(originalListener, listener);
                }
            }
            if (wcOpts.linkToDom) {
                // add or remove listener when component is attached / detached from DOM
                wcOpts.component.addConnectionListener({
                    origin: this,// origin prevents the listener from being registered twice
                    connected: () => {
                        this.eventHandler.addListener(listener as EventListenerOrEventListenerObject, options);
                    },
                    disconnected: () => {
                        this.off(listener);
                    }
                });
                if (!wcOpts.component.wasConnected) {
                    return;// addEventListener will be done when component will be inserted in the DOM
                }
                if (wcOpts.component.wasConnected && !wcOpts.component.isInitDone) {
                    // we are inside init, addEventListener will be done later
                    return;
                }
            }
        }
        this.eventHandler.addListener(listener as EventListenerOrEventListenerObject, options);
    }

    /**
     * register listener on the event
     * @returns this event handler
     */
    public off(listener: CustomEventListenerOrEventListenerObject<EventData>): void {
        const overriddenListener = this._overriddenListenersMap?.get(listener);
        this.eventHandler.removeListener((overriddenListener ?? listener) as EventListenerOrEventListenerObject);
    }

}

/**
 * Default EventHandler implementation that uses HTMLElement addEventListener removeEventListener and dispatchEvent
 */
class DomEventHandler<EventData, EventInitOptions extends CommonEventInit = CommonEventInit> implements EventHandler<EventData, EventInitOptions> {

    protected _wcHelper: WebComponentEventHandlerHelper<EventData>;
    constructor(protected _target: HTMLElement, public event: string, protected _defaultOptions?: EventInitOptions) {
        this._wcHelper = new WebComponentEventHandlerHelper(this);
    }

    // true if on was called at least once
    public onWasCalled = false;

    /**
     * register listener on the event
     * @returns this event handler
     */
    public on(listener: CustomEventListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): EventHandler<EventData> {
        this.onWasCalled = true;
        this._wcHelper.on(listener, options);// calls addListener
        return this;
    }

    /**
     * @deprecated do not call directly, it is used by _wcHelper (you are supposed to use "on")
     */
    public addListener(listener: CustomEventListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): void {
        this._target.addEventListener(this.event, listener as EventListenerOrEventListenerObject, options);
    }

    /**
     * register listener on the event
     * @returns this event handler
     */
    public off(listener: CustomEventListenerOrEventListenerObject<EventData>): EventHandler<EventData> {
        this._wcHelper.off(listener);// calls removeListener
        return this;
    }

    /**
     * @deprecated do not call directly, it is used by _wcHelper (you are supposed to use "off")
     */
    public removeListener(listener: CustomEventListenerOrEventListenerObject<EventData>): void {
        this._target.removeEventListener(this.event, listener as EventListenerOrEventListenerObject);
    }

    /**
     * emit CustomEvent with eventData
     */
    public emit(eventData: EventData, options?: EventInitOptions): void {
        this._target.dispatchEvent(new CustomEvent<EventData>(this.event, {
            detail: eventData,
            ...this._defaultOptions,
            ...options
        }));
    }

}

/** CommonListEventHandler with WebComponent options */
class ClientListEventHandler<EventData, EventInitOptions extends CommonEventInit = CommonEventInit> extends CommonListEventHandler<EventData, EventInitOptions> {

    protected _wcHelper: WebComponentEventHandlerHelper<EventData>;
    constructor(event: string, _defaultOptions?: EventInitOptions) {
        super(event, _defaultOptions);
        this._wcHelper = new WebComponentEventHandlerHelper(this);
    }

    /**
     * register listener on the event
     * @returns this event handler
     */
    public override on(listener: CommonListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): EventHandler<EventData> {
        this.onWasCalled = true;
        this._wcHelper.on(listener, options);// calls addListener
        return this;
    }

    /**
     * @deprecated do not call directly, it is used by _wcHelper (you are supposed to use "on")
     */
    public addListener(listener: CommonListenerOrEventListenerObject<EventData>, options?: AddDomEventListenerOptions): void {
        super.on(listener, options);
    }

    /**
     * register listener on the event
     * @returns this event handler
     */
    public override off(listener: CommonListenerOrEventListenerObject<EventData>): EventHandler<EventData> {
        this._wcHelper.off(listener);// calls removeListener
        return this;
    }

    /**
     * @deprecated do not call directly, it is used by _wcHelper (you are supposed to use "off")
     */
    public removeListener(listener: CommonListenerOrEventListenerObject<EventData>): void {
        super.off(listener);
    }

}